import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { List, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from '@/lib/AuthContext';

export default function SignInRequired() {
  const { loginWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);

  // Read error parameter from URL if redirected from OAuth callback
  const urlParams = new URLSearchParams(window.location.search);
  const errorParam = urlParams.get('error');

  const getErrorMessage = (error: string | null) => {
    if (!error) return null;
    switch (error) {
      case 'access_denied':
        return 'Google sign-in was canceled or declined.';
      case 'state_mismatch':
      case 'missing_auth_flow':
      case 'invalid_auth_flow':
        return 'Authentication session expired. Please try signing in again.';
      case 'oauth_not_configured':
        return 'Google OAuth is not configured on this server.';
      case 'token_exchange_failed':
      case 'invalid_user_profile':
      case 'auth_failed':
      default:
        return `Sign in failed (${error}). Please try again.`;
    }
  };

  const errorMessage = getErrorMessage(errorParam);

  const handleGoogleLogin = () => {
    setLoading(true);
    // Keep the current pathname as return_url if not root
    const currentPath = window.location.pathname !== '/' ? window.location.pathname : undefined;
    loginWithGoogle(currentPath);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:bg-[#0b0e14] flex items-center justify-center p-4 transition-colors">
      <Card className="w-full max-w-md shadow-xl border-slate-200 dark:border-[#202736] bg-white dark:bg-[#151921]">
        <CardHeader className="text-center pb-4">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-3">
            <List className="w-7 h-7 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900 dark:text-white font-headline">
            Welcome to Listify
          </CardTitle>
          <CardDescription className="text-slate-600 dark:text-slate-400">
            Sign in with your Google account to access your lists, folders, and notes.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 pt-2">
          {errorMessage && (
            <div className="p-3.5 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 text-sm text-red-700 dark:text-red-400 flex items-start gap-2.5">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
              <div>
                <strong className="font-semibold">Sign in notice:</strong> {errorMessage}
              </div>
            </div>
          )}

          <div className="pt-2">
            <Button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full h-12 flex items-center justify-center gap-3 border border-slate-300 dark:border-[#2c3548] rounded-xl shadow-xs bg-white hover:bg-slate-50 dark:bg-[#1b212c] dark:hover:bg-[#222938] text-slate-700 dark:text-slate-100 font-medium transition-all text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                  <span>Connecting to Google...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                    />
                  </svg>
                  <span>Continue with Google</span>
                </>
              )}
            </Button>
          </div>

          <p className="text-center text-xs text-slate-400 dark:text-slate-500 pt-2">
            Secure sign in powered by Google Identity Services
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
